import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import LoyaltyCard from './loyalty-card';
import './MemberCardModal.css';
import appleWalletIcon from '../../assets/images/loyalty-apple-wallet.svg';
import googleWalletIcon from '../../assets/images/loyalty-google-wallet.png';
import {
  ButtonUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import { CloseButtonCWUI } from '@next-order/next-order-components';

const MemberCardModal = ({
  isOpen,
  onClose,
  userLoyaltyDetails,
  userPoints,
  themeColors,
  storeSettings,
  userDetails,
  storeConfig,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isGeneratingPass, setIsGeneratingPass] = useState(false);

  useEffect(() => {
    // Check device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroidDevice = /android/i.test(userAgent);
    const isIOSDevice = /iPhone|iPad|iPod|Macintosh/i.test(userAgent);
    // Only consider it a desktop if it's not a mobile or tablet device
    const isDesktopDevice = !(
      isAndroidDevice ||
      isIOSDevice ||
      /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
    );

    setIsAndroid(isAndroidDevice);
    setIsIOS(isIOSDevice);
    setIsDesktop(isDesktopDevice);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      // Start the closing animation
      setIsClosing(true);
      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match this with the animation duration in CSS

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Don't render anything if the modal shouldn't be shown
  if (!shouldRender) return null;

  // Handle the close action
  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 280); // Slightly shorter than the animation to ensure smooth transition
  };

  // Generate wallet pass (Google or Apple)
  const handleAddToWallet = async (isApplePass = false) => {
    try {
      setIsGeneratingPass(true);

      const response = await fetch(
        `${storeConfig.cloudFunctionsUrl}/walletPassGenerator`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            storeId: storeConfig.storeId,
            userId: userDetails?._id,
            email: userDetails?.email,
            isApplePass,
          }),
        }
      );

      if (isApplePass) {
        // Handle Apple Wallet - download file
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `loyalty-card-${
            userDetails?.firstName || 'member'
          }.pkpass`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          console.error(
            'Failed to generate Apple Wallet pass:',
            response.statusText
          );
        }
      } else {
        // Handle Google Wallet - open URL with JWT
        const data = await response.json();
        if (data.success && data.signedJwt) {
          window.open(
            `https://pay.google.com/gp/v/save/${data.signedJwt}`,
            '_blank'
          );
        } else {
          console.error('Failed to generate Google Wallet pass:', data.error);
          window.open(
            'https://pay.google.com/payments/u/0/home/wallet/add?source=web',
            '_blank'
          );
        }
      }
    } catch (error) {
      console.error(
        `Error generating ${isApplePass ? 'Apple' : 'Google'} Wallet pass:`,
        error
      );
      if (isApplePass) {
      } else {
        window.open(
          'https://pay.google.com/payments/u/0/home/wallet/add?source=web',
          '_blank'
        );
      }
    } finally {
      setIsGeneratingPass(false);
    }
  };

  // Extract user information for the loyalty card
  const { firstName, lastName, memberId } = userLoyaltyDetails || {};
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const { imageThemeUrl } = storeSettings || {};

  // Determine if any wallet buttons should be displayed
  const showGoogleWallet = isAndroid || (isDesktop && !isIOS);
  const showAppleWallet = isIOS || (isDesktop && !isAndroid);
  const showAnyWallet = showGoogleWallet || showAppleWallet;

  return (
    <div className={`member-card-modal ${isClosing ? 'closing' : ''}`}>
      <div className='member-card-content'>
        <CloseButtonCWUI
          extendedClassName='mt-[-4px] ml-[-4px]'
          onClick={handleClose}
        />
        <CustomerCategoryTitleUI extendedClassName='member-card-header'>
          Member Card
        </CustomerCategoryTitleUI>

        <div className='member-card-wrapper'>
          <LoyaltyCard
            mainTextColor={themeColors.mainTextColor}
            labelColor={themeColors.labelTextColor || 'rgba(0, 0, 0, 0.55)'}
            bgColor={themeColors.backgroundColor}
            selectedImageTheme={imageThemeUrl}
            customReferralId={memberId || ''}
            userId={userDetails?._id || ''}
            customName={fullName || ''}
            customPoints={userPoints || 0}
          />
        </div>

        <div
          className={`wallet-button-container ${!showAnyWallet ? 'empty' : ''}`}
        >
          {/* Show Google Wallet button on Android devices or desktop */}
          {showGoogleWallet && (
            <ButtonUI
              fullWidth
              size='xl'
              rounded='xl'
              extendedClassName={isDesktop || !isAndroid ? 'mb-3' : 'mb-0'}
              fontSize='lg'
              style={{
                backgroundColor: 'black',
                color: 'white',
                border: 'none',
              }}
              title='Add to Google Wallet'
              onClick={() => handleAddToWallet(false)}
              disabled={isGeneratingPass}
            >
              <img
                src={googleWalletIcon}
                alt='Google Wallet'
                className='walletIcon google-wallet-icon'
              />
              <div className='walletButtonText'>
                {isGeneratingPass ? 'Redirecting...' : 'Add to Google Wallet'}
              </div>
            </ButtonUI>
          )}

          {/* Show Apple Wallet button on iOS or desktop devices */}
          {showAppleWallet && (
            <ButtonUI
              fullWidth
              size='xl'
              rounded='xl'
              extendedClassName={isDesktop || !isAndroid ? 'mb-0' : 'mb-4'}
              fontSize='lg'
              style={{
                backgroundColor: 'black',
                color: 'white',
                border: 'none',
              }}
              title='Add to Apple Wallet'
              onClick={() => handleAddToWallet(true)}
              disabled={isGeneratingPass}
            >
              <img
                src={appleWalletIcon}
                alt='Apple Wallet'
                className='walletIcon apple-wallet-icon'
              />
              <div className='walletButtonText'>
                {isGeneratingPass ? 'Dowloading...' : 'Add to Apple Wallet'}
              </div>
            </ButtonUI>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberCardModal;
